/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import type { FC } from 'react';
import React, { useState, useRef } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import Textfield from '@atlaskit/textfield/text-field';
import SearchIcon from '@atlaskit/icon/core/search';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import CrossIcon from '@atlaskit/icon/core/cross';
import IconButton from '@atlaskit/button/icon/button';
import { jsx, cssMap, cx } from '@atlaskit/css';

import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { isSpaceRestrictedError } from '@confluence/restrictions/entry-points/Query.ts';

import {
	ContentTreeSearchEmptyLoader,
	ContentTreeSearchErrorLoader,
} from '../entry-points/ContentTreeSearchStates.ts';

import { useContentTreeSearch } from './useContentTreeSearch.ts';
import { LoadingOverlay } from './LoadingOverlay.tsx';
import { ContentSearchResultList } from './ContentSearchResultList.tsx';

const styles = cssMap({
	pageTreeContainer: {
		overflowY: 'auto',
		maxHeight: '280px',
		// doing this to fix the drag indicator cutting issue
		marginLeft: token('space.negative.050'),
	},
	dialogContainer: {
		height: '280px',
	},
	searchResultContainer: {
		paddingLeft: token('space.050'),
		color: token('color.text.subtle'),
	},
	searchIconBefore: {
		paddingLeft: token('space.100'),
	},
	searchTextfield: {
		marginBlock: token('space.100'),
	},
	textfieldContainer: {
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
	},
});

export type SearchResult = {
	title?: string | null;
	id: string | null;
	hasRestrictions: boolean;
	hasInheritedRestrictions: boolean;
	displayEmoji: string;
	type?: string;
	subType?: string;
};
type ContentTreeSearchProps = React.PropsWithChildren<{
	spaceKey: string;
	contentId: string;
	onItemClick: (id: string, index?: number) => void;
	isModernizedDialog?: boolean;
	placeholder?: string;
	includeFolders?: boolean;
}>;

export const ContentTreeSearchBase: FC<ContentTreeSearchProps> = ({
	children,
	spaceKey,
	contentId,
	onItemClick,
	isModernizedDialog,
	placeholder,
	includeFolders,
}: ContentTreeSearchProps) => {
	const [searchText, setSearchText] = useState('');

	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const intl = useIntl();
	const inputRef = useRef<HTMLInputElement>(null);

	const { searchResult, loading, error } = useContentTreeSearch(
		searchText,
		'moveDialog',
		spaceKey,
		includeFolders,
	);

	const handleChange = (e: any) => {
		const inputValue = e.currentTarget.value || '';
		setSearchText(inputValue);
	};

	const onSearchResultClick = (id: string, index?: number) => {
		onItemClick(id, index);
		setSearchText('');
	};

	const renderPageTreeOrSearchResults = () => {
		if (error) {
			if (isSpaceRestrictedError(error) || isExpectedApolloError(error)) {
				// Mark error as handled since we're displaying a user-friendly error message
				markErrorAsHandled(error);
			}

			return (
				<ErrorDisplay error={error}>
					<ContentTreeSearchErrorLoader />
				</ErrorDisplay>
			);
		}
		if (searchResult && searchResult.length === 0) {
			return <ContentTreeSearchEmptyLoader />;
		}
		if (!searchResult) {
			return children;
		}
		if (searchResult.length > 0) {
			return (
				<Box xcss={styles.searchResultContainer}>
					<VisuallyHidden>
						<FormattedMessage
							{...i18n.searchResultsCount}
							values={{ count: searchResult.length }}
						/>
					</VisuallyHidden>
					<ContentSearchResultList
						pagesList={searchResult}
						spaceKey={spaceKey}
						contentId={contentId}
						onItemClick={onSearchResultClick}
					/>
				</Box>
			);
		}

		return null;
	};

	return (
		<Box data-vc="content-tree-search" {...ssrPlaceholderIdProp}>
			<Box id="contentTreeSearch" xcss={styles.searchTextfield}>
				<Flex columnGap="space.100">
					<Textfield
						ref={inputRef}
						aria-label={intl.formatMessage(i18n.searchLabel)}
						aria-describedby="contentTreeSearchInfo"
						placeholder={placeholder ?? intl.formatMessage(i18n.searchLabel)}
						onChange={handleChange}
						value={searchText}
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
						css={styles.textfieldContainer}
						elemBeforeInput={
							<Inline xcss={styles.searchIconBefore} alignBlock="center">
								<SearchIcon label="" color={token('color.icon.subtle')} />
							</Inline>
						}
						elemAfterInput={
							searchText ? (
								<IconButton
									appearance="subtle"
									spacing="compact"
									shape="circle"
									label={intl.formatMessage(i18n.clearSearch)}
									icon={() => <CrossIcon label="" size="small" color={token('color.icon')} />}
									onClick={() => setSearchText('')}
								/>
							) : null
						}
						isCompact
						testId="content-tree-search-input"
					/>
					<VisuallyHidden id="contentTreeSearchInfo">
						{intl.formatMessage(i18n.searchInputDescription)}
					</VisuallyHidden>
				</Flex>
			</Box>
			<LoadingOverlay loading={loading}>
				<Box xcss={cx(styles.pageTreeContainer, isModernizedDialog && styles.dialogContainer)}>
					{renderPageTreeOrSearchResults()}
				</Box>
			</LoadingOverlay>
		</Box>
	);
};

const i18n = defineMessages({
	searchLabel: {
		id: 'content-tree-search.placeholder',
		defaultMessage: 'Search by title',
		description: 'Placeholder text for content tree search input',
	},
	clearSearch: {
		id: 'content-tree-search.clear-search',
		defaultMessage: 'Clear search',
		description:
			'The text is shown as a tooltip on the clear button inside the content tree search input field, indicating that clicking it will erase the current search query.',
	},
	searchInputDescription: {
		id: 'content-tree-search.input-description',
		defaultMessage: 'Results will update as you type.',
		description: 'Aria description for content tree search input',
	},
	searchResultsCount: {
		id: 'content-tree-search.results-count',
		defaultMessage: '{count} {count, plural, one {result} other {results}} found',
		description: 'Screen reader only text with search results count in content tree search',
	},
});
